// DEPENDENCIES ---------------------------------------------------------------- //

import type { Theme } from '@mui/material';
import type { IconType } from 'react-icons';
import { BiSolidHeart, BiSolidMapPin, BiSolidUserCircle } from 'react-icons/bi';
import { MdPlayCircle, MdFlagCircle } from 'react-icons/md';

// TYPES ---------------------------------------------------------------- //

export type LocationPinVariant = 'pickup' | 'delivery' | 'driver' | 'favorite' | 'utility' | string;

export type MarkerPaletteKey = 'info' | 'success' | 'primary' | 'utility';

// CONSTANTS ---------------------------------------------------------------- //

const FALLBACK_MAIN = '#a0a4b0';
const FALLBACK_ALT = '#404860';

// HELPERS ---------------------------------------------------------------- //

/** Map a pin variant to the MUI palette key that drives its colors. */
export const getMarkerPaletteKey = (markerId: string): MarkerPaletteKey => {
  if (markerId === 'pickup') return 'info';
  if (markerId === 'delivery') return 'success';
  if (markerId === 'driver') return 'primary';
  return 'utility';
};

/** Map a pin variant to its icon component. */
export const getMarkerIcon = (markerId: string): IconType => {
  if (markerId === 'pickup') return MdPlayCircle;
  if (markerId === 'delivery') return MdFlagCircle;
  if (markerId === 'driver') return BiSolidUserCircle;
  if (markerId === 'favorite') return BiSolidHeart;
  return BiSolidMapPin;
};

const resolveThemeColor = (theme: Theme | undefined, paletteKey: MarkerPaletteKey, shade: 'main' | 'alt'): string => {
  if (paletteKey === 'utility') {
    if (shade === 'alt') return theme?.palette?.utility?.main || FALLBACK_ALT;
    return theme?.palette?.utility?.lighter || FALLBACK_MAIN;
  }
  const slot = (theme?.palette as any)?.[paletteKey];
  if (shade === 'alt') return slot?.darker || FALLBACK_ALT;
  return slot?.main || FALLBACK_MAIN;
};

/** Pin fill color (teardrop background). */
export const getMarkerBackgroundColor = (theme: Theme | undefined, markerId: string): string =>
  resolveThemeColor(theme, getMarkerPaletteKey(markerId), 'main');

/** Icon/foreground color rendered on top of the pin fill. */
export const getMarkerForegroundColor = (theme: Theme | undefined, markerId: string): string =>
  resolveThemeColor(theme, getMarkerPaletteKey(markerId), 'alt');

/** Title color for a detail bubble anchored to the pin. */
export const getMarkerTitleColor = (theme: Theme | undefined, markerId: string): string =>
  resolveThemeColor(theme, getMarkerPaletteKey(markerId), 'main');
